import { emailTranslationEntrySchema } from "@redotech/redo-marketing-templates-common/email-template-translation-fields";
import {
  emailTemplateSourceLocaleSchema,
  emailTemplateSupportedLocaleSchema,
} from "@redotech/redo-marketing-templates-common/email-template-translations";
import * as zExt from "@redotech/rpc/ext";
import * as z from "zod";

export const EMAIL_TEMPLATE_TRANSLATION_LANGUAGE_LIMIT = 20;

/** Translated text by entry key; blank keys fall back to the source text. */
const translationTextsSchema = z.record(
  z.string().max(500),
  z.string().max(50_000),
);

const localeTranslationSchema = z.object({
  locale: emailTemplateSupportedLocaleSchema,
  texts: translationTextsSchema,
});

export type EmailTemplateLocaleTranslation = z.infer<
  typeof localeTranslationSchema
>;

const localeTranslationsSchema = z
  .array(localeTranslationSchema)
  .max(EMAIL_TEMPLATE_TRANSLATION_LANGUAGE_LIMIT);

const targetLocalesSchema = z
  .array(emailTemplateSupportedLocaleSchema)
  .max(EMAIL_TEMPLATE_TRANSLATION_LANGUAGE_LIMIT);

const emailTemplateLocalizationSchema = z.object({
  sourceLocale: emailTemplateSourceLocaleSchema.optional(),
  /** The saved template's translatable strings. */
  entries: z.array(emailTranslationEntrySchema),
  /** One per stored locale copy, read back as texts by entry key. */
  translations: localeTranslationsSchema,
  /** Saved locales are being re-translated after a source change. */
  retranslating: z.boolean(),
});

export type EmailTemplateLocalization = z.infer<
  typeof emailTemplateLocalizationSchema
>;

export const getEmailTemplateLocalization = {
  name: "getEmailTemplateLocalization" as const,
  input: z.object({ emailTemplateId: zExt.objectId() }),
  output: emailTemplateLocalizationSchema,
};

/** Drafts AI translations for review; nothing is saved. */
export const translateEmailTemplate = {
  name: "translateEmailTemplate" as const,
  input: z.object({
    emailTemplateId: zExt.objectId(),
    sourceLocale: emailTemplateSourceLocaleSchema,
    targetLocales: targetLocalesSchema,
  }),
  output: z.object({ translations: localeTranslationsSchema }),
};

/** Replaces each listed locale's copy and deletes `deleteLocales`. */
export const saveEmailTemplateLocalization = {
  name: "saveEmailTemplateLocalization" as const,
  input: z.object({
    emailTemplateId: zExt.objectId(),
    sourceLocale: emailTemplateSourceLocaleSchema,
    translations: localeTranslationsSchema,
    deleteLocales: targetLocalesSchema,
  }),
  output: emailTemplateLocalizationSchema,
};
